/**
 * Google Analytics 4 (gtag.js) — scoped to the login/registration funnel only.
 *
 * Design constraints (REQUIREMENTS/API/plan/2026-07-20_plan_google-analytics-auth-funnel.md):
 * - **Consent Mode v2**: consent defaults to `denied`; we flip `analytics_storage`
 *   to `granted` only once the user accepts the analytics cookie category. gtag
 *   itself may load (Consent Mode sends cookieless pings until granted) but sets
 *   no `_ga` cookies before consent.
 * - **Production only**: gated on `GA_MEASUREMENT_ID` from the runtime config,
 *   which only the prod container injects — so GA never runs in dev/staging.
 * - **Auth-funnel only**: this module is driven exclusively by `AuthAnalytics`,
 *   which is mounted on the public auth pages, never behind the login.
 *
 * All functions are safe no-ops when there is no measurement id or no `window`.
 */

import { clearCookiesMatching } from './clearCookies'

type GtagConsentValue = 'granted' | 'denied'
// biome-ignore lint/suspicious/noExplicitAny: gtag's arg tuples are heterogeneous by design
type GtagArgs = any[]

declare global {
  interface Window {
    dataLayer?: GtagArgs[]
    gtag?: (...args: GtagArgs) => void
  }
}

const GTAG_SRC = 'https://www.googletagmanager.com/gtag/js'

let loaded = false

/** The GA4 measurement id from the runtime config, or null when analytics is off. */
export function getMeasurementId(): string | null {
  if (typeof window === 'undefined') return null
  const id = window.__RUNTIME_CONFIG__?.GA_MEASUREMENT_ID?.trim()
  return id ? id : null
}

/** True when a measurement id is configured (prod container only). */
export function isGaConfigured(): boolean {
  return getMeasurementId() !== null
}

function gtag(...args: GtagArgs): void {
  window.dataLayer = window.dataLayer || []
  // gtag pushes `arguments` verbatim — do not spread into an array.
  window.dataLayer.push(args)
}

/**
 * Idempotently bootstrap Consent Mode v2 (default denied) and load gtag.js.
 * Returns true if GA is now active, false if it's disabled (no id / no window).
 */
export function ensureGtagLoaded(): boolean {
  if (typeof window === 'undefined') return false
  const id = getMeasurementId()
  if (!id) return false
  if (loaded) return true

  window.dataLayer = window.dataLayer || []
  // Expose a real gtag so callers (and inline consent) share one dataLayer.
  window.gtag =
    window.gtag ||
    ((...args: GtagArgs) => {
      window.dataLayer?.push(args)
    })

  // Consent Mode v2 — must be set BEFORE `config`. Everything denied until the
  // user accepts the analytics category (see setAnalyticsConsent).
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    wait_for_update: 500,
  })
  gtag('js', new Date())
  // send_page_view: false — this is an SPA; AuthAnalytics fires page_view itself.
  gtag('config', id, { send_page_view: false })

  if (!document.querySelector(`script[data-gtag="${id}"]`)) {
    const s = document.createElement('script')
    s.async = true
    s.src = `${GTAG_SRC}?id=${encodeURIComponent(id)}`
    s.dataset.gtag = id
    document.head.appendChild(s)
  }

  loaded = true
  return true
}

/** Flip Consent Mode `analytics_storage` when the user grants/revokes analytics. */
export function setAnalyticsConsent(granted: boolean): void {
  if (typeof window === 'undefined' || !loaded) return
  const value: GtagConsentValue = granted ? 'granted' : 'denied'
  gtag('consent', 'update', { analytics_storage: value })
  // Best-effort removal of GA's `_ga` / `_ga_*` cookies on consent withdrawal.
  if (!granted) clearCookiesMatching(/^_ga/)
}

/** Manual SPA page_view for an auth route. */
export function trackPageView(path: string): void {
  if (typeof window === 'undefined' || !loaded) return
  gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  })
}

/** Conversion / funnel event (e.g. `login`, `sign_up`). */
export function trackEvent(name: string, params?: Record<string, unknown>): void {
  if (typeof window === 'undefined' || !loaded) return
  gtag('event', name, params ?? {})
}

/** Test-only: reset the module's loaded flag between cases. */
export function __resetGtagForTests(): void {
  loaded = false
}
